<template>
  <div class="signature-pad-container">
    <v-card outlined>
      <v-card-subtitle class="font-weight-bold d-flex align-center">
        <v-icon left>mdi-pen</v-icon>
        {{ label }}
        <v-spacer></v-spacer>
        <v-chip
          v-if="hasSignature"
          color="success"
          small
          outlined
        >
          <v-icon left small>mdi-check-circle</v-icon>
          Firmado
        </v-chip>
      </v-card-subtitle>
      
      <v-card-text>
        <!-- Canvas para firma -->
        <div class="signature-canvas-wrapper" ref="canvasWrapper">
          <canvas
            ref="canvas"
            @mousedown="startDrawing"
            @mousemove="draw"
            @mouseup="stopDrawing"
            @mouseleave="stopDrawing"
            @touchstart="handleTouchStart"
            @touchmove="handleTouchMove"
            @touchend="stopDrawing"
          ></canvas>
          
          <!-- Línea de guía -->
          <div class="signature-line"></div>
          
          <!-- Placeholder cuando está vacío -->
          <div v-if="!hasSignature && !isDrawing" class="signature-placeholder">
            <v-icon size="40" color="grey lighten-1">mdi-gesture</v-icon>
            <div class="text-caption grey--text mt-2">Firme aquí</div>
          </div>
        </div>
        
        <!-- Botones de acción -->
        <v-row class="mt-3" dense>
          <v-col cols="12">
            <v-btn
              color="warning"
              outlined
              small
              block
              @click="clear"
              :disabled="!hasSignature"
            >
              <v-icon left small>mdi-eraser</v-icon>
              Limpiar Firma
            </v-btn>
          </v-col>
        </v-row>
        
        <!-- Información adicional -->
        <v-row class="mt-2" dense v-if="showMetadata">
          <v-col cols="12">
            <v-text-field
              v-model="signerName"
              :label="'Nombre de ' + (label.includes('Cliente') ? 'Cliente' : 'Inspector')"
              prepend-icon="mdi-account"
              outlined
              dense
              hide-details
              @input="emitUpdate"
            ></v-text-field>
          </v-col>
        </v-row>
        
        <!-- Timestamp -->
        <div v-if="hasSignature && timestamp" class="text-caption grey--text mt-2 text-center">
          Firmado el {{ formatTimestamp(timestamp) }}
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'SignaturePad',
  
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Firma'
    },
    showMetadata: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: '#ffffff'
    },
    penColor: {
      type: String,
      default: '#000000'
    },
    penWidth: {
      type: Number,
      default: 2
    }
  },
  
  data() {
    return {
      isDrawing: false,
      hasSignature: false,
      lastX: 0,
      lastY: 0,
      signerName: '',
      timestamp: null,
      ctx: null
    };
  },
  
  watch: {
    value(newValue) {
      if (newValue && newValue !== this.getSignatureData()) {
        this.loadSignature(newValue);
      }
    }
  },
  
  mounted() {
    this.initCanvas();
    
    if (this.value) {
      this.loadSignature(this.value);
    }
    
    // Ajustar canvas en resize
    window.addEventListener('resize', this.handleResize);
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  
  methods: {
    initCanvas() {
      const canvas = this.$refs.canvas;
      const wrapper = this.$refs.canvasWrapper;
      
      // Establecer tamaño del canvas
      const dpr = window.devicePixelRatio || 1;
      const rect = wrapper.getBoundingClientRect();
      
      canvas.width = rect.width * dpr;
      canvas.height = 200 * dpr;
      canvas.style.width = rect.width + 'px';
      canvas.style.height = '200px';
      
      // Configurar contexto
      this.ctx = canvas.getContext('2d');
      this.ctx.scale(dpr, dpr);
      this.ctx.strokeStyle = this.penColor;
      this.ctx.lineWidth = this.penWidth;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
      
      // Fondo blanco
      this.ctx.fillStyle = this.backgroundColor;
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);
    },
    
    handleResize() {
      const currentData = this.getSignatureData();
      this.initCanvas();
      if (currentData) {
        this.loadSignature(currentData);
      }
    },
    
    startDrawing(e) {
      this.isDrawing = true;
      const pos = this.getPosition(e);
      this.lastX = pos.x;
      this.lastY = pos.y;
      
      // Comenzar nuevo trazo
      this.ctx.beginPath();
      this.ctx.moveTo(pos.x, pos.y);
    },
    
    draw(e) {
      if (!this.isDrawing) return;
      
      const pos = this.getPosition(e);
      
      this.ctx.lineTo(pos.x, pos.y);
      this.ctx.stroke();
      
      this.lastX = pos.x;
      this.lastY = pos.y;
      
      this.hasSignature = true;
    },
    
    stopDrawing() {
      if (this.isDrawing) {
        this.isDrawing = false;
        this.ctx.closePath();
        
        if (this.hasSignature) {
          this.timestamp = new Date().toISOString();
          this.emitUpdate();
        }
      }
    },
    
    handleTouchStart(e) {
      e.preventDefault();
      const touch = e.touches[0];
      this.startDrawing(touch);
    },
    
    handleTouchMove(e) {
      e.preventDefault();
      const touch = e.touches[0];
      this.draw(touch);
    },
    
    getPosition(e) {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      
      return {
        x: (e.clientX || e.pageX) - rect.left,
        y: (e.clientY || e.pageY) - rect.top
      };
    },
    
    clear() {
      const canvas = this.$refs.canvas;
      this.ctx.fillStyle = this.backgroundColor;
      this.ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      this.hasSignature = false;
      this.timestamp = null;
      this.emitUpdate();
    },
    
    getSignatureData() {
      if (!this.hasSignature) return '';
      
      const canvas = this.$refs.canvas;
      return canvas.toDataURL('image/png');
    },
    
    loadSignature(data) {
      if (!data) return;
      
      const img = new Image();
      img.onload = () => {
        const canvas = this.$refs.canvas;
        this.ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.ctx.drawImage(img, 0, 0, canvas.width / (window.devicePixelRatio || 1), 200);
        this.hasSignature = true;
      };
      img.src = data;
    },
    
    emitUpdate() {
      const signatureData = this.getSignatureData();
      
      this.$emit('input', signatureData);
      this.$emit('update', {
        signature: signatureData,
        signerName: this.signerName,
        timestamp: this.timestamp
      });
    },
    
    formatTimestamp(timestamp) {
      if (!timestamp) return '';
      
      const date = new Date(timestamp);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    // Método público para obtener todos los datos de la firma
    getSignatureInfo() {
      return {
        signature: this.getSignatureData(),
        signerName: this.signerName,
        timestamp: this.timestamp,
        hasSignature: this.hasSignature
      };
    }
  }
};
</script>

<style scoped>
.signature-pad-container {
  width: 100%;
}

.signature-canvas-wrapper {
  position: relative;
  width: 100%;
  height: 200px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  overflow: hidden;
}

canvas {
  display: block;
  cursor: crosshair;
  touch-action: none;
  position: relative;
  z-index: 1;
}

.signature-line {
  position: absolute;
  bottom: 40px;
  left: 20px;
  right: 20px;
  height: 1px;
  background: #ccc;
  pointer-events: none;
  z-index: 0;
}

.signature-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
  z-index: 0;
}

.v-card {
  height: 100%;
}
</style>
